import type { FC, FormEvent, ReactNode } from 'react'
import type { BurstPiece } from '@/components/contact/burst'

import { useEffect, useId, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, Check, LoaderCircle, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toPlatform } from '@/lib/platforms'
import { PlatformIcon } from '@/components/kit/platform-icon'
import { Spark } from '@/components/kit/spark'
import { Burst, makeBurst } from '@/components/contact/burst'
import { CONTACT_BUDGETS, CONTACT_PLATFORMS, PLAN_TO_BUDGET } from '@/constants/contact'

type Errors = Partial<Record<'name' | 'email' | 'message' | 'form', string>>

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const field = 'peer block w-full rounded-2xl border bg-paper px-5 text-base text-ink outline-none transition-[border-color,box-shadow] duration-200 focus:border-ember focus:ring-4 focus:ring-ember/15'

const floatingLabel = 'pointer-events-none absolute left-5 text-stone transition-all duration-200 peer-focus:top-3 peer-focus:translate-y-0 peer-focus:text-xs peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs'

interface FloatingFieldProps {
    id: string
    label: string
    error?: string
    children: ReactNode
    multiline?: boolean
}

const FloatingField: FC<FloatingFieldProps> = ({ id, label, error, children, multiline = false }) => {
    return (
        <div>
            <div className='relative'>
                {children}
                <label
                    htmlFor={id}
                    className={cn(floatingLabel, multiline ? 'top-5 text-base' : 'top-1/2 -translate-y-1/2 text-base')}
                >
                    {label}
                </label>
            </div>
            {error && (
                <p
                    id={`${id}-error`}
                    className='mt-2 pl-2 text-sm text-[#b42d07]'
                >
                    {error}
                </p>
            )}
        </div>
    )
}

export const ContactForm: FC = () => {
    const router = useRouter()
    const id = useId()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [brand, setBrand] = useState('')
    const [platforms, setPlatforms] = useState<Array<string>>([])
    const [budget, setBudget] = useState('')
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [errors, setErrors] = useState<Errors>({})
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
    const [pieces, setPieces] = useState<Array<BurstPiece>>([])
    const [planNote, setPlanNote] = useState('')

    useEffect(() => {
        if (!router.isReady) return

        const plan = typeof router.query.plan === 'string' ? router.query.plan.toLowerCase() : ''

        if (PLAN_TO_BUDGET[plan]) {
            setBudget(PLAN_TO_BUDGET[plan])
            setPlanNote(plan === 'enterprise' ? 'A custom crew' : `${plan.charAt(0).toUpperCase()}${plan.slice(1)} plan`)
        }
    }, [router.isReady, router.query.plan])

    const togglePlatform = (platform: string) => {
        setPlatforms(current => (current.includes(platform) ? current.filter(item => item !== platform) : [...current, platform]))
    }

    const validate = (): Errors => {
        const next: Errors = {}

        if (name.trim().length < 2) next.name = 'We will need a name to say hello.'
        if (!EMAIL_PATTERN.test(email.trim())) next.email = 'That email address looks a bit off.'
        if (message.trim().length < 10) next.message = 'A sentence or two more would help us a lot.'

        return next
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const found = validate()

        setErrors(found)

        if (Object.keys(found).length) {
            const first = Object.keys(found)[0]

            document.getElementById(`${id}-${first}`)?.focus()

            return
        }

        setStatus('sending')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    kind: 'brief',
                    name: name.trim(),
                    email: email.trim(),
                    brand: brand.trim(),
                    platforms,
                    budget,
                    message: message.trim(),
                    website
                })
            })

            const data = await response.json().catch(() => ({})) as { message?: string, errors?: Errors }

            if (!response.ok) {
                setErrors({ ...(data.errors ?? {}), form: data.message ?? 'Something went wrong. Please try again.' })
                setStatus('idle')

                return
            }

            setStatus('sent')

            if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) setPieces(makeBurst())
        }
        catch {
            setErrors({ form: 'We could not reach the server. Check your connection and try again.' })
            setStatus('idle')
        }
    }

    const startOver = () => {
        setName('')
        setEmail('')
        setBrand('')
        setPlatforms([])
        setBudget('')
        setMessage('')
        setErrors({})
        setPieces([])
        setStatus('idle')
    }

    if (status === 'sent') {
        return (
            <div
                role='status'
                className='relative flex min-h-[560px] flex-col items-center justify-center overflow-visible rounded-[2rem] border border-black/5 bg-paper p-8 text-center md:p-12'
            >
                <Burst pieces={pieces} />

                <span className='relative flex size-20 items-center justify-center rounded-full bg-ember text-ink'>
                    <Check
                        className='size-9'
                        strokeWidth={2.5}
                    />
                </span>
                <h2 className='mt-8 text-[clamp(2.25rem,4vw,3.25rem)] font-bold leading-[0.95] tracking-[-0.035em]'>
                    Cleared for take-off.
                </h2>
                <p className='mt-4 max-w-sm text-lg leading-relaxed text-stone'>
                    Thanks, {name.trim().split(' ')[0]}. Your forecast request is in. A strategist will reply to {email.trim()} within 24 hours with first thoughts.
                </p>
                <button
                    type='button'
                    onClick={startOver}
                    className='mt-8 inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-3 text-sm font-medium transition-colors hover:border-ink hover:bg-ink hover:text-cream'
                >
                    <RotateCcw className='size-4' />
                    Send another one
                </button>
            </div>
        )
    }

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            className='rounded-[2rem] border border-black/5 bg-paper p-6 shadow-[0_40px_80px_-60px_rgb(30_30_30/0.5)] md:p-10'
        >
            {planNote && (
                <p className='mb-6 inline-flex items-center gap-2 rounded-full bg-sand px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em]'>
                    <Spark className='size-2.5 text-ember' />
                    Looking at: {planNote}
                </p>
            )}

            <div className='grid gap-4 sm:grid-cols-2'>
                <FloatingField
                    id={`${id}-name`}
                    label='Your name'
                    error={errors.name}
                >
                    <input
                        id={`${id}-name`}
                        name='name'
                        autoComplete='name'
                        placeholder=' '
                        value={name}
                        onChange={event => setName(event.target.value)}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? `${id}-name-error` : undefined}
                        className={cn(field, 'h-16 pt-5', errors.name ? 'border-[#b42d07]' : 'border-ink/12')}
                    />
                </FloatingField>

                <FloatingField
                    id={`${id}-email`}
                    label='Email address'
                    error={errors.email}
                >
                    <input
                        id={`${id}-email`}
                        name='email'
                        type='email'
                        autoComplete='email'
                        inputMode='email'
                        placeholder=' '
                        value={email}
                        onChange={event => setEmail(event.target.value)}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? `${id}-email-error` : undefined}
                        className={cn(field, 'h-16 pt-5', errors.email ? 'border-[#b42d07]' : 'border-ink/12')}
                    />
                </FloatingField>
            </div>

            <div className='mt-4'>
                <FloatingField
                    id={`${id}-brand`}
                    label='Brand or website (optional)'
                >
                    <input
                        id={`${id}-brand`}
                        name='brand'
                        autoComplete='organization'
                        placeholder=' '
                        value={brand}
                        onChange={event => setBrand(event.target.value)}
                        className={cn(field, 'h-16 border-ink/12 pt-5')}
                    />
                </FloatingField>
            </div>

            <fieldset className='mt-8'>
                <legend className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Where do your people hang out?</legend>
                <div className='mt-3 flex flex-wrap gap-2'>
                    {CONTACT_PLATFORMS.map(platform => {
                        const icon = toPlatform(platform)

                        return (
                            <label
                                key={platform}
                                className='cursor-pointer'
                            >
                                <input
                                    type='checkbox'
                                    name='platforms'
                                    value={platform}
                                    checked={platforms.includes(platform)}
                                    onChange={() => togglePlatform(platform)}
                                    className='peer sr-only'
                                />
                                <span className='inline-flex items-center gap-2 rounded-full border border-ink/12 px-4 py-2.5 text-sm font-medium transition-[background-color,border-color,color] duration-200 hover:border-ink/40 peer-checked:border-ink peer-checked:bg-ink peer-checked:text-cream peer-focus-visible:ring-4 peer-focus-visible:ring-ember/30'>
                                    {icon && (
                                        <PlatformIcon
                                            name={icon}
                                            className='size-4'
                                        />
                                    )}
                                    {platform}
                                </span>
                            </label>
                        )
                    })}
                </div>
            </fieldset>

            <fieldset className='mt-8'>
                <legend className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Rough monthly budget</legend>
                <div className='mt-3 flex flex-wrap gap-2'>
                    {CONTACT_BUDGETS.map(option => (
                        <label
                            key={option}
                            className='cursor-pointer'
                        >
                            <input
                                type='radio'
                                name='budget'
                                value={option}
                                checked={budget === option}
                                onChange={() => setBudget(option)}
                                className='peer sr-only'
                            />
                            <span className='inline-flex items-center rounded-full border border-ink/12 px-4 py-2.5 text-sm font-medium transition-[background-color,border-color,color] duration-200 hover:border-ink/40 peer-checked:border-ember peer-checked:bg-ember peer-checked:text-ink peer-focus-visible:ring-4 peer-focus-visible:ring-ember/30'>
                                {option}
                            </span>
                        </label>
                    ))}
                </div>
            </fieldset>

            <div className='mt-8'>
                <FloatingField
                    id={`${id}-message`}
                    label='What would you like people to say about you?'
                    error={errors.message}
                    multiline
                >
                    <textarea
                        id={`${id}-message`}
                        name='message'
                        rows={5}
                        placeholder=' '
                        value={message}
                        onChange={event => setMessage(event.target.value)}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={errors.message ? `${id}-message-error` : undefined}
                        className={cn(field, 'resize-y pb-4 pt-8 leading-relaxed', errors.message ? 'border-[#b42d07]' : 'border-ink/12')}
                    />
                </FloatingField>
            </div>

            <div
                aria-hidden='true'
                className='absolute -left-[9999px] h-px w-px overflow-hidden'
            >
                <label htmlFor={`${id}-website`}>Website</label>
                <input
                    id={`${id}-website`}
                    name='website'
                    tabIndex={-1}
                    autoComplete='off'
                    value={website}
                    onChange={event => setWebsite(event.target.value)}
                />
            </div>

            {errors.form && (
                <p
                    role='alert'
                    className='mt-6 rounded-2xl bg-[#b42d07]/10 px-4 py-3 text-sm text-[#8f2405]'
                >
                    {errors.form}
                </p>
            )}

            <div className='mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-sm text-stone'>
                    We reply within 24 hours. We will never add you to a list you did not ask for.
                </p>
                <button
                    type='submit'
                    disabled={status === 'sending'}
                    className='group inline-flex h-14 items-center justify-center gap-4 rounded-full bg-ember pl-7 pr-2 text-base font-medium text-ink shadow-[0_14px_34px_-16px_rgb(255_92_0/0.8)] transition-colors hover:bg-[#ff6a14] disabled:opacity-70'
                >
                    {status === 'sending' ? 'Sending' : 'Get a forecast'}
                    <span className='flex size-10 items-center justify-center rounded-full bg-ink text-cream'>
                        {status === 'sending'
                            ? <LoaderCircle className='size-4 animate-spin' />
                            : <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' />}
                    </span>
                </button>
            </div>
        </form>
    )
}
